import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  IFiltersStepForm,
  IOptimizeStepForm,
  IRulesStepForm,
} from 'watchtower-ui/pages/OptimizationPages/planning/steps/utils';
import {
  getDefaultFiltersStepValues,
  getDefaultOptimizeStepValues,
  getDefaultRulesStepValues,
} from 'watchtower-ui/pages/OptimizationPages/planning/steps/utils';

export const PLAN_V2_KEY = 'optimization_plan_v2';

export const OPTIMIZATION_PLANNING_STEPS = {
  filters: 'filters',
  optimize: 'optimize',
  rules: 'rules',
  generate: 'generate',
} as const;

export const OptimizationPlanningStepsExpandedList = Object.keys(OPTIMIZATION_PLANNING_STEPS) as Array<
  keyof typeof OPTIMIZATION_PLANNING_STEPS
>;

export type OptimizationPlanV2 = {
  filters: IFiltersStepForm;
  optimize: IOptimizeStepForm;
  rules: IRulesStepForm;
};

export interface OptimizationState {
  plan: OptimizationPlanV2;
  isReady: boolean;
  multiStep: {
    currentStep: number;
    steps: [
      typeof OPTIMIZATION_PLANNING_STEPS.filters,
      typeof OPTIMIZATION_PLANNING_STEPS.optimize,
      typeof OPTIMIZATION_PLANNING_STEPS.rules,
      typeof OPTIMIZATION_PLANNING_STEPS.generate,
    ];
    isLastStep: boolean;
    isFinalSubmissionLoading: boolean;
  };
}

const initialState: OptimizationState = {
  plan: {
    filters: getDefaultFiltersStepValues(),
    optimize: getDefaultOptimizeStepValues(),
    rules: getDefaultRulesStepValues(),
  },
  isReady: false,
  multiStep: {
    currentStep: 0,
    steps: ['filters', 'optimize', 'rules', 'generate'],
    isLastStep: false,
    isFinalSubmissionLoading: false,
  },
};

const optimizationSlice = createSlice({
  name: 'optimization',
  initialState,
  reducers: {
    setPlan: (state, action: PayloadAction<OptimizationPlanV2>) => ({ ...state, plan: action.payload }),
    updateFilters: (state, action: PayloadAction<IFiltersStepForm>) => {
      state.plan.filters = action.payload;
    },
    updateOptimize: (state, action: PayloadAction<IOptimizeStepForm>) => {
      state.plan.optimize = action.payload;
    },
    updateRules: (state, action: PayloadAction<IRulesStepForm>) => {
      state.plan.rules = action.payload;
    },
    resetPlan: (state, action?: PayloadAction<OptimizationPlanV2>) => {
      state.plan = action?.payload ?? {
        filters: getDefaultFiltersStepValues(),
        optimize: getDefaultOptimizeStepValues(),
        rules: getDefaultRulesStepValues(),
      };
      sessionStorage.removeItem(PLAN_V2_KEY);
    },
    setReady: (state) => {
      state.isReady = true;
    },
    nextStep: (state) => {
      if (state.multiStep.currentStep < state.multiStep.steps.length - 1) {
        state.multiStep.currentStep += 1;
        state.multiStep.isLastStep = state.multiStep.currentStep === state.multiStep.steps.length - 1;
      }
    },
    previousStep: (state) => {
      if (state.multiStep.currentStep > 0) {
        state.multiStep.currentStep -= 1;
        state.multiStep.isLastStep = false;
      }
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0 && action.payload < state.multiStep.steps.length) {
        state.multiStep.currentStep = action.payload;
        state.multiStep.isLastStep = action.payload === state.multiStep.steps.length - 1;
      }
    },
  },
});

export const { setPlan, updateFilters, updateOptimize, updateRules, setReady, nextStep, previousStep, setCurrentStep } =
  optimizationSlice.actions;

export default optimizationSlice.reducer;
